/**
 * fieldRegistry.js — maps a config field's `control` name to the primitive component that
 * renders it
 */

import { InputField } from "../../primary/InputField/InputField";
import { SelectField } from "../../primary/SelectField/SelectField";
import { ChipGroup } from "../../secondary/ChipGroup/ChipGroup";
import { MultiSelect } from "../../primary/MultiSelect/multiSelect";
import { RepeatableTextList } from "../../secondary/RepeatableTextList/RepeatableTextList";
import { buildValidationRule } from "./validation";
import { resolveFieldOptions, resolveFieldDisabled } from "./configFormHelpers";

const registry = {};

export const registerControl = (name, Component, mapProps) => {
  registry[name] = { Component, mapProps };
};

export const getControl = (name) => registry[name];

registerControl("text", InputField, ({ field, form, errors, onFieldChange, register, customValidators, disabled, hasPermission }) => {
  const rule = buildValidationRule(field.validation, customValidators);
  return {
    id: field.key,
    name: field.key,
    type: field.type || "text",
    defaultValue: form[field.key],
    suffix: field.suffix,
    placeholder: field.placeholder,
    disabled: resolveFieldDisabled(field, { disabled, hasPermission }),
    required: rule ? false : !!field.required,
    dataTestId: field.dataTestId || field.key,
    error: errors?.[field.key]?.message,
    inputRef: register ? register(rule || {}) : undefined,
    onChange: (e) => onFieldChange(field.key, e.target.value),
  };
});

registerControl("select", SelectField, ({ field, form, errors, onFieldChange, register, customValidators, disabled, hasPermission, extra }) => {
  const rule = buildValidationRule(field.validation, customValidators);
  return {
    id: field.key,
    name: field.key,
    options: resolveFieldOptions(field, form, extra),
    value: form[field.key] ?? "",
    placeholder: field.placeholder,
    disabled: resolveFieldDisabled(field, { disabled, hasPermission }),
    required: field.required,
    searchable: !!field.searchable,
    dataTestId: field.dataTestId || `select_${field.key}`,
    error: errors?.[field.key]?.message,
    inputRef: register ? register(rule || {}) : undefined,
    onChange: (e) => onFieldChange(field.key, e.target.value),
  };
});

registerControl("chip", ChipGroup, ({ field, form, errors, onFieldChange, disabled, hasPermission }) => ({
  name: field.key,
  options: field.options || [],
  selected: form[field.key],
  multiple: !!field.multiple,
  disabled: resolveFieldDisabled(field, { disabled, hasPermission }),
  errors,
  onToggle: (_key, nextValue) => onFieldChange(field.key, nextValue),
}));

registerControl("multiselect", MultiSelect, ({ field, form, onMultiChange, submitErrors, disabled, hasPermission }) => ({
  id: field.key,
  name: field.key,
  optionValue: field.options || [],
  defaultValue: form[field.key] || [],
  disabled: resolveFieldDisabled(field, { disabled, hasPermission }),
  required: field.required,
  showCheckbox: field.showCheckbox ?? true,
  displayValue: field.displayValue || "value",
  showSelectAll: !!field.showSelectAll,
  onSelect: (opts) => onMultiChange(field.key, opts),
  onRemove: (opts) => onMultiChange(field.key, opts),
  customErrors: field.errorFlagKey ? !!submitErrors?.[field.errorFlagKey] : false,
  customErrorsMsg: field.errorMessage,
}));

registerControl("repeatable-text", RepeatableTextList, ({ field, form, onFieldChange, submitErrors, disabled, hasPermission }) => {
  const values = Array.isArray(form[field.key]) && form[field.key].length ? form[field.key] : [""];

  return {
    name: field.key,
    values,
    disabled: resolveFieldDisabled(field, { disabled, hasPermission }),
    addLabel: field.addLabel || "+ Add",
    canAdd: typeof field.canAddRow === "function" ? field.canAddRow(values) : true,
    errors: submitErrors?.[field.errorFlagKey] || [],
    onItemChange: (index, value) => {
      const next = [...values];
      next[index] = value;
      onFieldChange(field.key, next);
    },
    onAdd: () => onFieldChange(field.key, [...values, ""], { skipValidate: true }),
    onRemove: (index) =>
      onFieldChange(
        field.key,
        values.filter((_, i) => i !== index)
      ),
  };
});
